import { useForm, Controller } from "react-hook-form";
import { Box, Stack, TextField, Typography } from "@mui/material";
import AnimateButton from "../extended/AnimateButton";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import useUser from "../../../hooks/useUser";
import useRateLimiter from "../../../hooks/useRateLimiter";
import SubmitButton from "../buttons/SubmitButton";
import { useSnackbarHelper } from "../Snackbar";
import { requestFCMToken } from "../../../utils/fireBaseUtils";
import { useState } from "react";

// ========================|| FIREBASE - TWO FACTOR AUTHENTICATION ||======================== //

const TwoFactorAuthentication = () => {
  const [isLoading, setLoading] = useState(false);
  const { verifyOtp } = useUser();
  const showSnackbar = useSnackbarHelper();
  const { isLocked, remainingLockout, recordFailure, recordSuccess } =
    useRateLimiter("otpAttempts");

  const validationSchema = Yup.object().shape({
    otp: Yup.array()
      .of(
        Yup.string()
          .length(1, "Must be exactly 1 character")
          .required("Required"),
      )
      .length(4, "Must be exactly 4 characters"),
  });

  const { handleSubmit, control, setValue, getValues } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      otp: ["", "", "", ""],
    },
  });

  const handleChange = (value: string, index: number) => {
    const currentOtp: any = getValues("otp");
    currentOtp[index] = value;
    setValue("otp", currentOtp);

    if (value && index < currentOtp.length - 1) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    } else if (value === "" && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const onFormSubmit = async (values: any) => {
    if (isLocked) return;

    const { otp } = values;
    const otpString = otp.join("");
    const email: string = sessionStorage.getItem("verifyEmail") || "";

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showSnackbar("Session expired. Please log in again.", "error");
      return;
    }

    try {
      setLoading(true);

      let webFcmToken = "";
      try {
        webFcmToken = await Promise.race([
          requestFCMToken(),
          new Promise<string>((resolve) => setTimeout(() => resolve(""), 5000)),
        ]);
      } catch (error) {
        console.log(error);
      }

      const data = await verifyOtp({ otp: otpString, email, webFcmToken, isSite: true });
      recordSuccess();
      const message = data?.message || "Success";
      setLoading(false);
      showSnackbar(message, "success");
    } catch (error: any) {
      setLoading(false);
      recordFailure();
      console.error(error?.response?.data?.message);
      const message = error?.response?.data?.message || "An error occurred";

      showSnackbar(message, "error");
    }
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      <Stack
        sx={{ mb: 3 }}
        spacing={4}
        direction={"row"}
        justifyContent={"center"}
      >
        {Array.from({ length: 4 }).map((_, index) => (
          <Controller
            key={index}
            name={`otp.${index}`}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderRadius: "12px" },
                    fontSize: "1rem",
                    fontFamily: "'Arial', sans-serif",
                    backgroundColor: "f5f5f500",
                    height: "3.2em",
                    width: "3.2em",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.875rem",
                  },
                  "& .MuiInputBase-input": {
                    textAlign: "center",
                  },
                }}
                id={`otp-${index}`}
                type="text"
                inputProps={{ maxLength: 1 }}
                value={field.value}
                onChange={(e) => handleChange(e.target.value, index)}
                required
                disabled={isLocked}
              />
            )}
          />
        ))}
      </Stack>

      {isLocked && (
        <Typography
          variant="body2"
          sx={{ color: "error.main", textAlign: "center", mb: 1 }}
        >
          Too many failed attempts. Try again in {remainingLockout} seconds.
        </Typography>
      )}

      <Box sx={{ mt: 6 }}>
        <AnimateButton>
          <SubmitButton
            disabled={isLoading || isLocked}
            disableElevation
            fullWidth
            size="large"
            type="submit"
            variant="contained"
          >
            {isLocked ? `Wait ${remainingLockout}s` : "Verify & Proceed"}
          </SubmitButton>
        </AnimateButton>
      </Box>
    </form>
  );
};

export default TwoFactorAuthentication;
